import React from "react";
import type { StatsNodeLink } from "../../../types/graphStatsTypes";
import { STATS_COLORS, getSuccessRateColor } from "../../../types/graphStatsTypes";

// Static dimensions matching the original graph components
const SCENE_SIZE = 150;
const ASSET_SIZE = 80;
const LINE_WIDTH = 4;

interface StatsLineProps {
  link: StatsNodeLink;
  dimmed?: boolean;
}

const getCenterPosition = (node: StatsNodeLink["from"]): { x: number; y: number } => {
  const size = node.type === "scene" ? SCENE_SIZE : ASSET_SIZE;
  return {
    x: node.x + size / 2,
    y: node.y + size / 2,
  };
};

const StatsLine: React.FC<StatsLineProps> = ({ link, dimmed = false }) => {
  const { from, to } = link;
  const { x: x1, y: y1 } = getCenterPosition(from);
  const { x: x2, y: y2 } = getCenterPosition(to);

  // Base line color - use the success high color for consistency
  const baseColor = STATS_COLORS.HIGH;

  const isNotVisited = from.visited === false || to.visited === false;
  const toHasStatsData = to.stats?.successRate != null;

  // Determine line color based on the "to" node
  let stroke: string = baseColor;

  if (isNotVisited) {
    stroke = STATS_COLORS.NOT_VISITED;
  } else if (to.type === "asset" && toHasStatsData && to.stats) {
    // Has actual stats - color based on success rate
    if (to.stats.successRate < 80) {
      stroke = getSuccessRateColor(to.stats.successRate);
    }
    // else keep green
  } else if (to.type === "asset" && !to.isScored) {
    // No stats data AND not a scored interaction type -> purple
    stroke = STATS_COLORS.NON_SCORED;
  }

  // For session view: if either node is not visited, use dashed line
  const isDashed = isNotVisited;

  // Calculate opacity: dimmed takes priority, then not visited, then normal
  const lineOpacity = dimmed ? 0.1 : isDashed ? 0.5 : 0.8;

  return (
    <g>
      <line
        x1={x1}
        x2={x2}
        y1={y1 + LINE_WIDTH / 2}
        y2={y2 + LINE_WIDTH / 2}
        stroke={stroke}
        strokeWidth={LINE_WIDTH}
        strokeDasharray={isDashed ? "8,4" : undefined}
        opacity={lineOpacity}
      />
    </g>
  );
};

export default StatsLine;
